/**
 * The vocabulary of the call client, plus the structural shims for the four
 * browser APIs TypeScript's DOM lib does not describe.
 *
 * WHY THE SHIMS ARE STRUCTURAL CASTS AND NOT `declare global`:
 * WebRTC Encoded Transform, WebCodecs audio and the OPFS synchronous access
 * handle are all either absent from `lib.dom.d.ts` at this TypeScript version or
 * present in a shape that changes between versions. A `declare global` interface
 * merge is a coin flip — if the lib later ships `RTCRtpSender.transform` with a
 * slightly different type, every file in this directory stops compiling and the
 * fix is not local. So the types below are plain interfaces that describe only
 * what we use, and the call sites cast. That keeps a lib upgrade from being a
 * lane-wide breakage, and it makes the exact surface we depend on readable in
 * one place instead of inferred from usage.
 *
 * THE WORKER TOPOLOGY, because the message types only make sense with it:
 *
 *     main thread            xform worker            sink worker
 *   ┌────────────┐  init   ┌──────────────┐        ┌───────────────┐
 *   │ transport  │────────►│ MEDIA PATH   │        │ OPFS + /media │
 *   │ telemetry  │  init   │ enqueue,     │ batch  │ chunker       │
 *   │ control    │────────►│ copy, batch  │═══════►│ uploader      │
 *   └────────────┘         └──────────────┘ Message└───────────────┘
 *          ▲                                Channel        │
 *          └──────────── counters, gaps, first-playout ─────┘
 *
 * The batch path is a DIRECT MessageChannel between the two workers. Audio never
 * touches the UI thread on the primary path — not for perf theatre, but because
 * a structured clone on the main thread lands in the same event loop as React
 * rendering, and a long layout would then show up as recorder jitter.
 */
import type { PayloadKind, RtcStatsSample, SessionState, TurnMark, RecordingDiagnosticCode } from '@voiceai/wire'

export type TrackName = 'user' | 'assistant'

/**
 * How we are capturing audio, in descending order of fidelity.
 *
 * `encoded-transform` is the only one that yields the bytes that actually
 * crossed the network. The other two are re-encodes of a decoded stream: honest,
 * usable, and NOT evidence of what the provider emitted. The UI says which one
 * is in use for exactly that reason.
 */
export type RecorderPath = 'encoded-transform' | 'webcodecs-opus' | 'pcm16' | 'none'

// ============================================================================
// main <-> workers
// ============================================================================

/**
 * One postMessage from the transform worker: ~8 encoded packets (160 ms) of one
 * track. Batched because one message per 20 ms packet is 50 structured clones a
 * second per track, and the sink has nothing useful to do with a single packet.
 *
 * `rtp` is the RAW 32-bit RTP timestamp per packet, in the same order as
 * `packets`. Unwrapping the 32-bit rollover into a monotone 64-bit timeline is
 * the sink's job — it is the only place that holds enough history to do it.
 *
 * `epochMicrosFirst` is the clock anchor: wall-clock microseconds observed when
 * the first packet of THIS batch was seen. Pairing it with `rtp[0]` is the
 * (rtpTimestamp, epoch) anchor the server regresses on, and at one anchor per
 * 160 ms it is far denser than the ~2 s the design asks for.
 */
export interface CaptureBatch {
  t: 'batch'
  track: TrackName
  kind: PayloadKind
  packets: Uint8Array[]
  rtp: number[]
  epochMicrosFirst: number
}

export interface XformInit {
  t: 'init'
  /** The far end of the MessageChannel to the sink worker. */
  port: MessagePort
  /** Packets per postMessage. 8 => 160 ms => ~6 messages/s/track. */
  batchFrames: number
}

/**
 * main thread -> transform worker.
 *
 * `stop` is not a teardown request — the worker is terminated separately. It asks
 * for two things that only this worker can do: flush the partial batch (up to
 * `batchFrames - 1` packets per track sit in `pending` waiting for a packet that,
 * at the end of a call, never arrives), and then put the end-of-stream sentinel
 * on the port.
 */
export type XformIn = XformInit | { t: 'stop' }

/**
 * transform worker -> sink worker, over the batch port.
 *
 * `eos` IS AN ORDERING PRIMITIVE and it is the only reason this union exists.
 * `{t:'stop'}` reaches the sink on the WORKER's message queue while batches
 * arrive on the PORT's, and there is no ordering guarantee between two queues —
 * so the transform worker's final batch can be posted before the stop and still
 * be delivered after it, which is how up to 160 ms per track used to be dropped
 * with no chunk, no seq and therefore no gap recorded anywhere. A single
 * MessagePort is FIFO, so a sentinel posted after the last batch is delivered
 * after the last batch, always. The sink waits for it before its final flush and
 * then knows the port is drained rather than hoping it is.
 *
 * The wait is BOUNDED. This worker lives in the media path; if it is wedged or
 * already gone, teardown carries on without it and anything that arrives later is
 * reported as a gap instead of being discarded.
 */
export type XformOut = CaptureBatch | { t: 'eos' }

export interface SinkInit {
  t: 'init'
  sessionId: string
  /** Absolute ws:// or wss:// URL, token already applied. FIRST connect only. */
  mediaUrl: string
  /**
   * Same URL carrying the long-lived reconnect token, used for every RE-connect.
   *
   * Two URLs rather than one because the two credentials have deliberately
   * different lifetimes: the first-connect token expires in two minutes, which is
   * right for a socket that opens immediately and useless for one that has to come
   * back at minute six. Before this the uploader re-dialled its original URL
   * forever, so a drop past T+120s could never recover and everything still in
   * OPFS was stranded.
   */
  mediaReconnectUrl: string
  /** Target media milliseconds per chunk. ~2000. */
  chunkMs: number
  /** Present on the primary path; null when the fallback posts batches direct. */
  port: MessagePort | null
}

export type SinkIn =
  | SinkInit
  /** Control said `ready`; here is where the server thinks we are. Open /media. */
  | { t: 'open'; ackSeq: Record<TrackName, number> }
  | { t: 'ack'; track: TrackName; seq: number }
  /** Fallback path only: the tap runs on the main thread. */
  | CaptureBatch
  /** Drain the batch port, flush, ship, report, stop reconnecting. Answered with
   *  `stopped` even if nothing was ever initialised, because the main thread
   *  waits for that before terminating the worker. */
  | { t: 'stop' }

export type SinkOut =
  | { t: 'diagnostic'; code: RecordingDiagnosticCode; errorName?: string }
  | { t: 'counters'; counters: RecorderCounters }
  | { t: 'gap'; track: TrackName; fromSeq: number; toSeq: number; reason: string }
  | { t: 'done'; track: TrackName; lastSeq: number; totalPackets: number }
  /**
   * The first assistant packet of a burst, which is the closest thing the
   * browser has to "the model started speaking, audibly, here". Tagged with the
   * RTP timestamp because the browser cannot know `response_id` at this moment —
   * the packet arrives before any control-channel relay could. The server joins
   * RTP -> response_id. See docs/architecture.md §7.
   */
  | { t: 'assistant-burst'; rtp: string; epochMs: number }
  | { t: 'log'; level: 'info' | 'warn' | 'error'; message: string }
  /** Everything has been flushed, the socket is closed and the lock is released.
   *  The main thread waits for this before terminating the worker, because
   *  terminating mid-flush would discard the tail of the recording. */
  | { t: 'stopped' }

/** What the diagnostics panel shows about the recorder. Cheap to render, so it
 *  is a full snapshot rather than a delta. */
export interface RecorderCounters {
  chunks: Record<TrackName, number>
  acked: Record<TrackName, number>
  packets: Record<TrackName, number>
  bytes: Record<TrackName, number>
  /** Chunks staged but not yet acknowledged. This is the backlog, i.e. latency. */
  backlog: Record<TrackName, number>
  bufferedAmount: number
  uplink: 'off' | 'connecting' | 'up' | 'down'
  /** OPFS staging is working. False means a socket drop becomes real loss. */
  staged: boolean
  /** We hold the per-session Web Lock. False means another tab owns the upload. */
  locked: boolean
  /** Genuine unrecoverable gaps reported so far. Should be 0. */
  gaps: number
}

export function emptyCounters(): RecorderCounters {
  return {
    chunks: { user: 0, assistant: 0 },
    acked: { user: -1, assistant: -1 },
    packets: { user: 0, assistant: 0 },
    bytes: { user: 0, assistant: 0 },
    backlog: { user: 0, assistant: 0 },
    bufferedAmount: 0,
    uplink: 'off',
    staged: false,
    locked: false,
    gaps: 0,
  }
}

// ============================================================================
// The snapshot the page renders
// ============================================================================

export type CallPhase =
  | 'idle'
  | 'checking'
  | 'blocked'
  | 'starting'
  | 'live'
  | 'ending'
  | 'ended'
  | 'failed'

export interface TranscriptLine {
  /** OpenAI item id, or a synthetic one for locally-echoed text. */
  itemId: string
  who: TrackName
  text: string
  final: boolean
  /** When the line FIRST appeared, browser clock — display only, kept across
   *  partial→final updates so the stamp is when speech started, not ended. */
  atMs: number
}

export interface ToolActivity {
  callId: string
  name: string
  /** Operator-written display label/blurb off the wire's `tool` message —
   *  informational furniture for the card; null when the tool has none. */
  displayName: string | null
  displayDescription: string | null
  state: 'running' | 'ok' | 'error'
  startedAtMs: number
  /** Set when the ok/error event lands, so the chip can show a duration. */
  endedAtMs: number | null
  /**
   * How many transcript lines existed when the call started — the chip's
   * position in the conversation. Lines carry no timestamps, so the anchor is
   * recorded at arrival time instead of computed later.
   */
  anchorIndex: number
}

/**
 * A SCREEN CARD from the built-in `show_info` tool: content the MODEL composed
 * for the caller's eyes, arrived whole on one `{t:'card'}` control message.
 *
 * NOT A TOOL RESULT, and the distinction is the trust boundary: arguments and
 * results never ride the control channel (a public embed visitor holds it), but
 * this card is the same trust class as the words the model speaks — the model
 * choosing to display something is the same act as choosing to say it. The body
 * is a small markdown subset, bounded at the executor; rendering it goes through
 * the whitelist renderer in lib/mini-markdown.tsx and nothing else.
 *
 * LIVE-ONLY, like transcript lines: a card sent while the control socket is down
 * is dropped, and honesty about that lives in the executor's return value — the
 * model is told `displayed: false` and can say the information out loud instead.
 */
export interface CardActivity {
  /** The provider's function-call id — the dedupe key, so a re-attached sideband
   *  replaying the frame replaces the card instead of duplicating it. */
  callId: string
  /** Optional heading. Null when the model sent none — absent, not empty. */
  title: string | null
  /** The markdown-subset body. Never HTML; see lib/mini-markdown.tsx. */
  body: string
  /** When the card FIRST arrived, browser clock — display furniture only. */
  atMs: number
  /**
   * How many transcript lines existed when the card arrived — its position in
   * the conversation. Same mechanism as ToolActivity's: lines carry no
   * timestamps, so the anchor is recorded at arrival time, not computed later.
   */
  anchorIndex: number
}

/** Everything the UI needs, in one immutable object. Replaced, never mutated,
 *  so `useSyncExternalStore` can compare by identity. */
/**
 * Which leg of the start sequence a connecting call is on, for a widget that
 * wants its "Connecting…" line to say something. Coarse on purpose — each value
 * is a stretch a human can actually perceive (hundreds of ms to seconds), in
 * start()'s own order: `checking` the browser, `security` for the session
 * create (which includes the customer's init webhook — the slow, variable leg)
 * plus the mic prompt beside it, `audio` for the peer/SDP/ICE work, `joining`
 * once the answer is applied and the sockets are coming up. Null outside a
 * start — including once the call is live.
 */
export type StartStage = 'checking' | 'security' | 'audio' | 'joining'

export interface CallSnapshot {
  phase: CallPhase
  /** See StartStage. Null unless the call is starting. */
  startStage: StartStage | null
  /**
   * The outcome of a greeting-render call (CallOptions.purpose), once the DO
   * sends it; null until then and on every ordinary call.
   */
  greetingRender: { status: 'saved' | 'failed'; reason?: string; durationMs?: number; sha256?: string; heard?: string } | null
  sessionId: string | null
  /** DO-owned session state. Null until the first `state` message. */
  serverState: SessionState | null
  serverStateDetail: string | null
  startedAtMs: number | null
  endedAtMs: number | null
  error: string | null
  /** Non-fatal things worth saying out loud in the UI. */
  notices: readonly string[]
  transcript: readonly TranscriptLine[]
  tools: readonly ToolActivity[]
  cards: readonly CardActivity[]
  speaking: { user: boolean; assistant: boolean }
  muted: boolean
  recorderPath: RecorderPath
  counters: RecorderCounters
  stats: RtcStatsSample | null
  /** Minimum-RTT clock sample against the DO. */
  clock: { offsetMs: number; rttMs: number } | null
  pc: { connection: RTCPeerConnectionState | null; ice: RTCIceConnectionState | null }
  controlUp: boolean
  turns: readonly TurnMark[]
  /**
   * A PERSON HAS THE CALL. Set when the DO says somebody in the panel took it
   * over: the AI leg is gone, the person's voice plays in place of the agent's,
   * and the peer connection failing no longer ends the call. Stays true until
   * the call ends; there is no hand-back to the AI yet.
   */
  takenOver: boolean
}

// ============================================================================
// Structural shims — see the header for why these are not global merges
// ============================================================================

export interface EncodedFrameMetadata {
  rtpTimestamp?: number
  /** Populated for RECEIVED frames only. Never trust it on the sender side. */
  sequenceNumber?: number
  payloadType?: number
  synchronizationSource?: number
}

export interface EncodedAudioFrameLike {
  /** The Opus packet. Reading this AFTER the frame has been enqueued is not
   *  safe in Chromium — the underlying frame is moved into the packetizer and
   *  the accessor throws. See workers/xform.ts. */
  data: ArrayBuffer
  /** Legacy alias for the RTP timestamp, still the only source in some builds. */
  timestamp: number
  getMetadata(): EncodedFrameMetadata
}

export interface RtcTransformer {
  readable: ReadableStream<EncodedAudioFrameLike>
  writable: WritableStream<EncodedAudioFrameLike>
  options: { track: TrackName; side: 'sender' | 'receiver' } | undefined
}

export interface RtcTransformEvent {
  transformer: RtcTransformer
}

export interface ScriptTransformCtor {
  new (worker: Worker, options?: unknown, transfer?: unknown[]): unknown
}

/** OPFS synchronous access handle. Worker-only, and the only OPFS write API
 *  that does not allocate a stream per chunk. */
export interface SyncAccessHandle {
  read(buffer: ArrayBufferView, options?: { at?: number }): number
  write(buffer: ArrayBufferView, options?: { at?: number }): number
  truncate(size: number): void
  getSize(): number
  flush(): void
  close(): void
}

export interface WcEncodedChunk {
  byteLength: number
  timestamp: number
  type: 'key' | 'delta'
  copyTo(dest: ArrayBufferView): void
}

export interface WcAudioEncoder {
  encode(data: unknown): void
  configure(config: unknown): void
  flush(): Promise<void>
  close(): void
  readonly state: 'unconfigured' | 'configured' | 'closed'
}

export interface WcAudioEncoderCtor {
  new (init: {
    output: (chunk: WcEncodedChunk, meta?: unknown) => void
    error: (err: DOMException) => void
  }): WcAudioEncoder
  isConfigSupported(config: unknown): Promise<{ supported?: boolean }>
}

export interface WcAudioDataCtor {
  new (init: {
    format: 'f32' | 's16'
    sampleRate: number
    numberOfFrames: number
    numberOfChannels: number
    timestamp: number
    data: BufferSource
  }): { close(): void }
}
